import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import LearningScreen from './LearningScreen';
import CheckpointScreen from './CheckpointScreen';
import JournalEntryScreen from './JournalEntryScreen';
import GoalsScreen from './GoalsScreen';
import SpeedbumpsScreen from './SpeedbumpsScreen';
import WhichAreYouScreen from './WhichAreYouScreen';
import FirstNameScreen from './FirstNameScreen';
import CommitmentScreen from './CommitmentScreen';
import OptimalPaceScreen from './OptimalPaceScreen';
import OptimalPaceRaceScreen from './OptimalPaceRaceScreen';
import OptimalPaceSoloScreen from './OptimalPaceSoloScreen';
import OptimalPaceIsolatedScreen from './OptimalPaceIsolatedScreen';
import OptimalPaceLearningScreen from './OptimalPaceLearningScreen';
import MembershipSelectionScreen from './MembershipSelectionScreen';
import MentalResilienceScreen from './MentalResilienceScreen';
import SafeSprintingScreen from './SafeSprintingScreen';
import TestimonialScreen from './TestimonialScreen';
import CommunityMembersScreen from './CommunityMembersScreen';
import MembershipDetailsScreen from './MembershipDetailsScreen';
import MembershipSummaryScreen from './MembershipSummaryScreen';
import StudyDetailsScreen from './StudyDetailsScreen';
import AsSeenInScreen from './AsSeenInScreen';
import MediaAppearanceScreen from './MediaAppearanceScreen';
import CompleteScreen from './CompleteScreen';
import LeaderboardScreen from './LeaderboardScreen';
import MembershipEntryScreen from './MembershipEntryScreen';
import DashboardScreen from './DashboardScreen';
import SprintRecoveryProtocolScreen from './SprintRecoveryProtocolScreen';
import UserInfoScreen from './UserInfoScreen';

const ScreenContainer = styled.div`
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
  
  &.fade-in {
    animation: fadeIn 0.2s ease-in-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ErrorContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.screenPadding};
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.error};
  margin-bottom: ${theme.spacing.md};
`;

const ErrorMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

const ScreenRenderer = ({ 
  screenData, 
  checkpointData,
  onResponse,
  onNext,
  onJournalEntry,
  onSafeSprintingData,
  userProgress = {},
  isLoading = false 
}) => {
  // React Hooks must be at the top level
  const [showError, setShowError] = useState(false);
  const [displayData, setDisplayData] = useState({ screenData: null, checkpointData: null });
  
  // Keep track of previous screen data to avoid flashing
  useEffect(() => {
    if (screenData || checkpointData) {
      // We have new data, update display immediately
      setDisplayData({ screenData, checkpointData });
      setShowError(false);
    }
    // If we have no data but are loading, keep previous data visible
  }, [screenData, checkpointData]);
  
  // Separate effect to handle error display with delay
  useEffect(() => {
    // Clear error immediately if we have data or are loading
    if (screenData || checkpointData || isLoading) {
      setShowError(false);
      return;
    }
    
    // No current data and not loading - check if we have previous display data
    if (displayData.screenData || displayData.checkpointData) {
      // We have previous data to show, don't show error
      setShowError(false);
      return;
    }
    
    // No data at all (current or previous) and not loading
    // Start error timer with longer delay to avoid false positives during transitions
    const timer = setTimeout(() => {
      // Double-check conditions before showing error
      if (!screenData && !checkpointData && !isLoading && 
          !displayData.screenData && !displayData.checkpointData) {
        setShowError(true);
      }
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [screenData, checkpointData, isLoading, displayData]);

  // Use display data instead of direct props to avoid flashing
  const currentScreenData = displayData.screenData;
  const currentCheckpointData = displayData.checkpointData;

  // Show loading state
  if (isLoading && !screenData && !checkpointData) {
    return (
      <ScreenContainer>
        <ErrorContainer>
          <ErrorMessage>Loading...</ErrorMessage>
        </ErrorContainer>
      </ScreenContainer>
    );
  }
  
  // Show error only after delay
  if (showError) {
    return (
      <ScreenContainer>
        <ErrorContainer>
          <ErrorTitle>Screen Not Found</ErrorTitle>
          <ErrorMessage>
            This screen could not be loaded. Please try again or contact support.
          </ErrorMessage>
        </ErrorContainer>
      </ScreenContainer>
    );
  }
  
  // If we don't have any display data and haven't shown error yet, show nothing
  if (!currentScreenData && !currentCheckpointData && !showError) {
    return null;
  }

  // If we have checkpoint data, determine which component to render
  if (currentCheckpointData) {
    
    // Special case: journal_entry checkpoints should use JournalEntryScreen
    if (currentCheckpointData.type === 'journal_entry') {
      return (
        <ScreenContainer className="fade-in">
          <JournalEntryScreen
            data={currentCheckpointData}
            onJournalEntry={onJournalEntry}
            onNext={onNext}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }

    // Special case: goals checkpoints use dedicated multi-step GoalsScreen
    if (currentCheckpointData.type === 'goals') {
      return (
        <ScreenContainer className="fade-in">
          <GoalsScreen
            data={currentCheckpointData}
            onResponse={onResponse}
            onNext={onNext}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }

    // Special case: speedbumps checkpoints use dedicated SpeedbumpsScreen
    if (currentCheckpointData.type === 'speedbumps') {
      return (
        <ScreenContainer className="fade-in">
          <SpeedbumpsScreen
            data={currentCheckpointData}
            onResponse={onResponse}
            onNext={onNext}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }

    // Special case: which_are_you checkpoints use dedicated WhichAreYouScreen
    if (currentCheckpointData.type === 'which_are_you') {
      return (
        <ScreenContainer className="fade-in">
          <WhichAreYouScreen
            data={currentCheckpointData}
            onResponse={onResponse}
            onNext={onNext}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }

    // Special case: first_name checkpoints use dedicated FirstNameScreen
    if (currentCheckpointData.type === 'first_name') {
      return (
        <ScreenContainer className="fade-in">
          <FirstNameScreen
            data={currentCheckpointData}
            onResponse={onResponse}
            onNext={onNext}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }

    // Special case: commitment checkpoints use dedicated CommitmentScreen
    if (currentCheckpointData.type === 'commitment') {
      return (
        <ScreenContainer className="fade-in">
          <CommitmentScreen
            data={currentCheckpointData}
            onResponse={onResponse}
            onNext={onNext}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }
    
    // All other checkpoint types use CheckpointScreen
    return (
      <ScreenContainer className="fade-in">
        <CheckpointScreen
          data={currentCheckpointData}
          onResponse={onResponse}
          onNext={onNext}
          userProgress={userProgress}
        />
      </ScreenContainer>
    );
  }

  // If we have screen data, determine which screen component to render
  if (currentScreenData) {
    const screenType = currentScreenData.type;
    const screenKey = currentScreenData.key;
    

    // Special case: complete screen gets its own component regardless of type
    if (screenKey === 'complete') {
      return (
        <ScreenContainer className="fade-in">
          <CompleteScreen
            data={currentScreenData}
            userProgress={userProgress}
          />
        </ScreenContainer>
      );
    }

    try {
      switch (screenType) {
        case 'learning':
          return (
            <ScreenContainer className="fade-in">
              <LearningScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'journal_entry':
          return (
            <ScreenContainer className="fade-in">
              <JournalEntryScreen
                data={currentScreenData}
                onJournalEntry={onJournalEntry}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'optimal_pace':
          return (
            <ScreenContainer className="fade-in">
              <OptimalPaceScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'optimal_pace_race':
          return (
            <ScreenContainer className="fade-in">
              <OptimalPaceRaceScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'optimal_pace_solo':
          return (
            <ScreenContainer className="fade-in">
              <OptimalPaceSoloScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'optimal_pace_isolated':
          return (
            <ScreenContainer className="fade-in">
              <OptimalPaceIsolatedScreen
                data={currentScreenData}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'optimal_pace_learning':
          return (
            <ScreenContainer className="fade-in">
              <OptimalPaceLearningScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'membership_selection':
          return (
            <ScreenContainer className="fade-in">
              <MembershipSelectionScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'mental_resilience':
          return (
            <ScreenContainer className="fade-in">
              <MentalResilienceScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'safe_sprinting':
          return (
            <ScreenContainer className="fade-in">
              <SafeSprintingScreen
                data={currentScreenData}
                onNext={onNext}
                onSafeSprintingData={onSafeSprintingData}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'testimonial':
          return (
            <ScreenContainer className="fade-in">
              <TestimonialScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'community_members':
          return (
            <ScreenContainer className="fade-in">
              <CommunityMembersScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'membership_details':
          return (
            <ScreenContainer className="fade-in">
              <MembershipDetailsScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'membership_summary':
          return (
            <ScreenContainer className="fade-in">
              <MembershipSummaryScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'study_details':
          return (
            <ScreenContainer className="fade-in">
              <StudyDetailsScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'as_seen_in':
          return (
            <ScreenContainer className="fade-in">
              <AsSeenInScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'media_appearance':
          return (
            <ScreenContainer className="fade-in">
              <MediaAppearanceScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'leaderboard':
          return (
            <ScreenContainer className="fade-in">
              <LeaderboardScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'membership_entry':
          return (
            <ScreenContainer className="fade-in">
              <MembershipEntryScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'dashboard':
          return (
            <ScreenContainer className="fade-in">
              <DashboardScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'sprint_recovery_protocol':
          return (
            <ScreenContainer className="fade-in">
              <SprintRecoveryProtocolScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        case 'user_info':
          return (
            <ScreenContainer className="fade-in">
              <UserInfoScreen
                data={currentScreenData}
                onNext={onNext}
                userProgress={userProgress}
              />
            </ScreenContainer>
          );

        default:
          return (
            <ScreenContainer className="fade-in">
              <ErrorContainer>
                <ErrorTitle>Unknown Screen Type</ErrorTitle>
                <ErrorMessage>
                  Screen type "{screenType}" is not supported.
                </ErrorMessage>
              </ErrorContainer>
            </ScreenContainer>
          );
      }
    } catch (error) {
      console.error('Error rendering screen:', error);
      return (
        <ScreenContainer className="fade-in">
          <ErrorContainer>
            <ErrorTitle>Rendering Error</ErrorTitle>
            <ErrorMessage>
              An error occurred while loading this screen. Please try again.
            </ErrorMessage>
          </ErrorContainer>
        </ScreenContainer>
      );
    }
  }

  // Fallback error
  return (
    <ScreenContainer className="fade-in">
      <ErrorContainer>
        <ErrorTitle>No Data</ErrorTitle>
        <ErrorMessage>
          No screen or checkpoint data available.
        </ErrorMessage>
      </ErrorContainer>
    </ScreenContainer>
  );
};

export default ScreenRenderer; 